import { Container, makeStyles, Typography } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DatePickerValidator from '../LeaveApplication/component/Validator/DatePickerValidator';
import Button from '@material-ui/core/Button';
import React from 'react';
import MUIDataTable from 'mui-datatables';
import { getMuiTheme, pxToRem, tableStyle } from '../UIComponent/CommonStyle';
import { getCompLeaveReqApprovalHistory } from './api/ApprovalAPI';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import moment from 'moment';
import {
    CompLeaveApprovalCollection,
    CompLeaveApprovalHistoryCollection,
    CompLeaveApprovalHistoryReportCollection,
} from '../Function/CompLeaveApproval';
import {
    setReqApprovalHistoryColumns,
    setReqApprovalHistoryOptions,
} from './component/TableSetting';
import GenerateCSV from './component/GenerateCSV';

const useStyles = makeStyles((theme) => ({
    ...tableStyle(theme),
    container: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(4),
    },
    datePicker: {
        width: '25%',
        paddingRight: '20px',
    },
    datePickerInput: {
        textAlign: 'left',
        padding: '1rem 1rem',
        fontSize: pxToRem(14),
        border: '1px solid #B8B8B8',
        borderRadius: 5,
        outline: 'none',
        height: 'fit-content',
        minWidth: '100%',
        [breakpoints.up('md')]: {
            fontSize: pxToRem(16),
            height: pxToRem(61),
        },
    },
    datePickerCalendar: {
        border: '1px solid #B8B8B8',
        fontSize: 8,
        [breakpoints.up('md')]: {
            fontSize: 10,
        },
    },
    searchForm: {
        display: 'flex',
        padding: '0 0 10px',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: 0,
    },
    searchButton: {
        height: 'fit-content',
        marginRight: '10px',
    },
    exportButton: {
        height: 'fit-content',
    },
    approvalHistoryHeader: {
        display: 'flex',
    },
    circularProgress: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(1),
    },
}));

const breakpoints = createBreakpoints({});

export default function ApprovalHistory({ showStaffNo }) {
    const form = React.useRef();
    const classes = useStyles();
    const intl = useIntl();

    const today = moment();
    const lastMonth = moment().subtract(1, 'months');
    const [searchFromDate, setSearchFromDate] = React.useState({
        day: Number(lastMonth.format('DD')),
        month: Number(lastMonth.format('MM')),
        year: Number(lastMonth.format('YYYY')),
    });
    const [searchToDate, setSearchToDate] = React.useState({
        day: Number(today.format('DD')),
        month: Number(today.format('MM')),
        year: Number(today.format('YYYY')),
    });
    const [showTable, setShowTable] = React.useState(false);
    const [tableData, setTableData] = React.useState([]);
    const [reportData, setReportData] = React.useState([]);
    const showApproveRejectOnly = true;

    const columns = setReqApprovalHistoryColumns(showStaffNo, intl, showApproveRejectOnly);
    const options = setReqApprovalHistoryOptions(intl);

    React.useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = async () => {
        if (searchFromDate && searchToDate) {
            setShowTable(false);
            const getApprovesResponse = await getCompLeaveReqApprovalHistory(
                searchFromDate,
                searchToDate
            );
            if (getApprovesResponse.status === 200) {
                setTableData(
                    CompLeaveApprovalHistoryCollection(
                        getApprovesResponse.data,
                        intl
                    )
                );
                setReportData(
                    CompLeaveApprovalHistoryReportCollection(
                        getApprovesResponse.data,
                        intl
                    )
                );
            } else {
                setTableData([]);
                setReportData([]);
            }
            setShowTable(true);
        }
    };

    return (
        <Container maxWidth={false} className={classes.container}>
            <MuiThemeProvider theme={getMuiTheme()}>
                <div className={classes.approvalHistoryHeader}>
                    <Typography
                        component='h1'
                        variant='h5'
                        align='left'
                        style={{ fontWeight: 600 }}
                    >
                        {intl.formatMessage({ id: 'navigation.approval_history' }) + ' - ' + intl.formatMessage({ id: 'navigation.ot_compensation' })}
                    </Typography>
                    <ValidatorForm
                        className={classes.searchForm}
                        ref={form}
                        onSubmit={handleSearch}
                        onError={(errors) => errors}
                        style={{ position: 'relative', zIndex: '999' }}
                    >
                        <FormattedMessage id='common.from'>
                            {(message) => (
                                <DatePickerValidator
                                    wrapperClassName={classes.datePicker}
                                    value={searchFromDate}
                                    onChange={setSearchFromDate}
                                    inputPlaceholder={message + ' *'}
                                    shouldHighlightWeekends
                                    inputClassName={classes.datePickerInput}
                                    calendarClassName={
                                        classes.datePickerCalendar
                                    }
                                    calendarPopperPosition='bottom'
                                    validators={['required']}
                                />
                            )}
                        </FormattedMessage>
                        <FormattedMessage id='common.to'>
                            {(message) => (
                                <DatePickerValidator
                                    wrapperClassName={classes.datePicker}
                                    value={searchToDate}
                                    onChange={setSearchToDate}
                                    inputPlaceholder={message + ' *'}
                                    shouldHighlightWeekends
                                    inputClassName={classes.datePickerInput}
                                    calendarClassName={
                                        classes.datePickerCalendar
                                    }
                                    calendarPopperPosition='bottom'
                                    validators={['required']}
                                />
                            )}
                        </FormattedMessage>
                        <Button
                            onClick={handleSearch}
                            variant='contained'
                            className={classes.searchButton}
                        >
                            {intl.formatMessage({ id: 'table.search' })}
                        </Button>
                        {showTable ? (
                            <Button
                                variant='contained'
                                className={classes.exportButton}
                            >
                                <GenerateCSV
                                    csvData={reportData}
                                    fromDate={searchFromDate}
                                    toDate={searchToDate}
                                    fullHistory={false}
                                />
                            </Button>
                        ) : (
                            <div />
                        )}
                    </ValidatorForm>
                </div>
                {showTable ? (
                    <MUIDataTable
                        title={intl.formatMessage({
                            id: 'table.employee_list',
                        })}
                        data={tableData}
                        columns={columns}
                        options={options}
                    />
                ) : (
                    <div className={classes.circularProgress}>
                        <CircularProgress />
                    </div>
                )}
            </MuiThemeProvider>
        </Container>
    );
}
